import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {Icons, Images} from '../../assets';
import {DashboardLayout} from '../../components';
import {FilterPill} from '../../components/filter-pill';
import {LocationAutocomplete} from '../../components/location-autocomplete';
import {TagAutocomplete} from '../../components/tag-autocomplete';
import {Button, ButtonSize, ButtonType, ButtonVariant} from '../../components/button';
import {Input, InputAppearance, InputShape, InputVariant} from '../../components/input';
import {getPublicProfilePath, ROUTES} from '../../constants/routes';
import {useEmployerCandidate} from '../../contexts/employer-candidate.provider';
import {CandidateProfile, SavedCandidate} from '../../models';
import {CandidateSearchParams, GetSkillsParams, ListSavedCandidatesParams, SaveCandidateParams, SkillServiceImpl} from '../../services';
import type {CandidateSearchResultType} from '../../services/employer-candidate';

const DEFAULT_LIMIT = 10;
const ROWS_PER_PAGE = 2;
const SAVED_SYNC_LIMIT = 50;

type ActiveFilters = {
  location?: string;
  skills: string[];
  industries: string[];
  experienceMin?: number;
  experienceMax?: number;
};

const EMPTY_FILTERS: ActiveFilters = {skills: [], industries: []};

// Curated industry options for the Industries filter autocomplete (no master list endpoint yet).
const INDUSTRIES = [
  'Software',
  'Information Technology',
  'Finance',
  'Banking',
  'Healthcare',
  'Education',
  'Manufacturing',
  'Retail',
  'Marketing & Advertising',
  'Consulting',
  'Real Estate',
  'Hospitality',
  'Media & Entertainment',
  'Telecommunications',
  'Automotive',
  'Energy',
  'Legal',
  'Non-Profit',
  'Government',
  'Construction',
  'Logistics & Supply Chain',
  'Pharmaceuticals',
  'Insurance',
  'E-commerce',
  'Aerospace',
  'Agriculture',
  'Design',
  'Human Resources',
  'Sales',
  'Engineering',
];

const parseYears = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

const SearchCandidatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeRole, setActiveRole] = useState('');
  const [bookmarkingCandidates, setBookmarkingCandidates] = useState<Record<string, boolean>>({});
  const [candidates, setCandidates] = useState<CandidateProfile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedCandidatesError, setSavedCandidatesError] = useState<string | null>(null);
  const [savedCandidatesMap, setSavedCandidatesMap] = useState<Record<string, SavedCandidate>>({});
  const [savedCandidatesSyncing, setSavedCandidatesSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [skillsTags, setSkillsTags] = useState<string[]>([]);
  const [industriesTags, setIndustriesTags] = useState<string[]>([]);
  const [experienceMinInput, setExperienceMinInput] = useState('');
  const [experienceMaxInput, setExperienceMaxInput] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(EMPTY_FILTERS);
  const [deepMode, setDeepMode] = useState(false);
  const [searchSummary, setSearchSummary] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [lastResultType, setLastResultType] = useState<CandidateSearchResultType | undefined>(undefined);
  const [cardsPerRow, setCardsPerRow] = useState(4);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const {listSavedCandidates, removeSavedCandidate, saveCandidate, searchCandidates} = useEmployerCandidate();

  const pageSize = cardsPerRow * ROWS_PER_PAGE || DEFAULT_LIMIT;
  // Read page size via a ref inside fetchCandidates so that cardsPerRow/pageSize changing on
  // mount/resize does NOT recreate fetchCandidates and re-fire the active search (which caused
  // a duplicate request, e.g. limit=8 then limit=6).
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  const fetchCandidates = useCallback(
    async (term: string, page: number, append: boolean, resultType?: CandidateSearchResultType) => {
      if (append) {
        setIsFetchingMore(true);
      } else {
        setIsLoading(true);
      }
      setError(null);
      try {
        const params = new CandidateSearchParams({
          limit: pageSizeRef.current,
          page,
          query: term,
          deep: deepMode,
          ...(resultType !== undefined && {resultType}),
          ...(activeFilters.location ? {location: activeFilters.location} : {}),
          ...(activeFilters.skills.length ? {skills: activeFilters.skills} : {}),
          ...(activeFilters.industries.length ? {industries: activeFilters.industries} : {}),
          ...(activeFilters.experienceMin === undefined ? {} : {experienceMin: activeFilters.experienceMin}),
          ...(activeFilters.experienceMax === undefined ? {} : {experienceMax: activeFilters.experienceMax}),
        });
        const response = await searchCandidates(params);
        setCandidates(previousCandidates => (append ? [...previousCandidates, ...response.data] : response.data));
        setHasMore(response.hasMore);
        setCurrentPage(response.page);
        setLastResultType(response.resultType);
        if (!append) {
          setSearchSummary(response.searchSummary);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to fetch candidates right now. Please try again in a moment.';
        setError(message);
        setCandidates([]);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [searchCandidates, activeFilters, deepMode],
  );

  const syncSavedCandidates = useCallback(async () => {
    setSavedCandidatesSyncing(true);
    setSavedCandidatesError(null);
    try {
      const response = await listSavedCandidates(new ListSavedCandidatesParams({limit: SAVED_SYNC_LIMIT, page: 1}));
      const mapped = response.data.reduce<Record<string, SavedCandidate>>((acc, savedCandidate) => {
        if (savedCandidate.candidateProfile.id) {
          acc[savedCandidate.candidateProfile.id] = savedCandidate;
        }
        return acc;
      }, {});
      setSavedCandidatesMap(mapped);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load saved candidates. Please refresh.';
      setSavedCandidatesError(message);
    } finally {
      setSavedCandidatesSyncing(false);
    }
  }, [listSavedCandidates]);

  useEffect(() => {
    syncSavedCandidates();
  }, [syncSavedCandidates]);

  useEffect(() => {
    const updateCardsPerRow = () => {
      const width = window.innerWidth;
      let columns = 1;

      if (width >= 1536) {
        columns = 5;
      } else if (width >= 1280) {
        columns = 4;
      } else if (width >= 1024) {
        columns = 3;
      } else if (width >= 768) {
        columns = 2;
      }

      setCardsPerRow(columns);
    };

    updateCardsPerRow();
    window.addEventListener('resize', updateCardsPerRow);

    return () => {
      window.removeEventListener('resize', updateCardsPerRow);
    };
  }, []);

  useEffect(() => {
    if (!activeRole) return;
    fetchCandidates(activeRole, 1, false, undefined);
  }, [activeRole, fetchCandidates]);

  useEffect(() => {
    if (!hasMore || isFetchingMore) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !isFetchingMore) {
          fetchCandidates(activeRole, currentPage + 1, true, lastResultType);
        }
      },
      {threshold: 1.0},
    );

    const current = loaderRef.current;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, [activeRole, currentPage, fetchCandidates, hasMore, isFetchingMore, lastResultType]);

  const handleBookmarkToggle = async (candidate: CandidateProfile) => {
    const candidateId = candidate.id;
    if (!candidateId) {
      return;
    }

    const previousMap = savedCandidatesMap;
    const existingSavedCandidate = previousMap[candidateId];

    const optimisticMap = {...previousMap};
    if (existingSavedCandidate) {
      delete optimisticMap[candidateId];
    } else {
      optimisticMap[candidateId] = new SavedCandidate({candidateProfile: candidate});
    }
    setSavedCandidatesMap(optimisticMap);

    setBookmarkingCandidates(prev => ({...prev, [candidateId]: true}));
    setSavedCandidatesError(null);
    try {
      if (existingSavedCandidate) {
        await removeSavedCandidate(existingSavedCandidate.id);
        return;
      }

      const savedCandidate = await saveCandidate(new SaveCandidateParams({candidateProfileId: candidateId}));
      setSavedCandidatesMap(prev => ({...prev, [candidateId]: savedCandidate}));
    } catch (err) {
      setSavedCandidatesMap({...previousMap});
      const message = err instanceof Error ? err.message : 'Unable to update saved candidates. Please try again or refresh the page.';
      setSavedCandidatesError(message);
    } finally {
      setBookmarkingCandidates(prev => ({...prev, [candidateId]: false}));
    }
  };

  const skillService = useMemo(() => new SkillServiceImpl(), []);

  const getSkillSuggestions = useCallback(
    async (query: string): Promise<string[]> => {
      const response = await skillService.getSkills(new GetSkillsParams({page: 1, size: 8, search: query}));
      const skills = response.data ?? [];
      return skills.map(skill => skill?.name).filter((name): name is string => Boolean(name));
    },
    [skillService],
  );

  const getIndustrySuggestions = useCallback(async (query: string): Promise<string[]> => {
    const lower = query.trim().toLowerCase();
    return INDUSTRIES.filter(industry => industry.toLowerCase().includes(lower)).slice(0, 8);
  }, []);

  const buildFiltersFromInputs = useCallback(
    (): ActiveFilters => ({
      location: locationInput.trim() || undefined,
      skills: skillsTags,
      industries: industriesTags,
      experienceMin: parseYears(experienceMinInput),
      experienceMax: parseYears(experienceMaxInput),
    }),
    [locationInput, skillsTags, industriesTags, experienceMinInput, experienceMaxInput],
  );

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const term = searchQuery.trim();
    if (!term) {
      setHasSearched(false);
      setCandidates([]);
      return;
    }
    setHasSearched(true);
    setCandidates([]);
    setCurrentPage(1);
    setHasMore(false);
    setLastResultType(undefined);
    setSearchSummary(undefined);
    // Snapshot the filter inputs so pagination of this search reuses the same filters.
    setActiveFilters(buildFiltersFromInputs());
    setActiveRole(term);
  };

  // Committing filters updates the active snapshot; the activeRole effect re-runs the search
  // automatically (fetchCandidates depends on activeFilters), so applying a filter immediately
  // refreshes results when a search is active — LinkedIn-style.
  const commitFilters = (next: ActiveFilters) => {
    setCandidates([]);
    setCurrentPage(1);
    setHasMore(false);
    setActiveFilters(next);
  };

  const applyFilters = () => commitFilters(buildFiltersFromInputs());

  const clearLocation = () => {
    setLocationInput('');
    commitFilters({...buildFiltersFromInputs(), location: undefined});
  };
  const clearSkills = () => {
    setSkillsTags([]);
    commitFilters({...buildFiltersFromInputs(), skills: []});
  };
  const clearIndustries = () => {
    setIndustriesTags([]);
    commitFilters({...buildFiltersFromInputs(), industries: []});
  };
  const clearExperience = () => {
    setExperienceMinInput('');
    setExperienceMaxInput('');
    commitFilters({...buildFiltersFromInputs(), experienceMin: undefined, experienceMax: undefined});
  };
  const clearAllFilters = () => {
    setLocationInput('');
    setSkillsTags([]);
    setIndustriesTags([]);
    setExperienceMinInput('');
    setExperienceMaxInput('');
    commitFilters(EMPTY_FILTERS);
  };

  const experienceSummary =
    activeFilters.experienceMin !== undefined || activeFilters.experienceMax !== undefined
      ? `${activeFilters.experienceMin ?? 0}–${activeFilters.experienceMax ?? '∞'} yrs`
      : undefined;
  const hasActiveFilters =
    Boolean(activeFilters.location) ||
    activeFilters.skills.length > 0 ||
    activeFilters.industries.length > 0 ||
    experienceSummary !== undefined;

  const inputClass =
    'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-[#2678FA] focus:outline-none focus:ring-1 focus:ring-[#2678FA]';

  const listSurfaceClass = 'border-gray-100 bg-white';
  const dividerClass = 'divide-gray-100';
  const rowHoverClass = 'hover:bg-gray-50';
  const primaryTextClass = 'text-gray-900';
  const secondaryTextClass = 'text-gray-600';
  const mutedTextClass = 'text-gray-500';

  const showInitialState = !hasSearched;
  const showEmptyState = hasSearched && !isLoading && !error && candidates.length === 0;

  const skelClass = 'bg-gray-200';
  const renderRowSkeleton = (key: string) => (
    <div className="flex items-center gap-4 px-5 py-4" key={key}>
      <div className={`h-14 w-14 flex-shrink-0 animate-pulse rounded-full ${skelClass}`} />
      <div className="flex-1 space-y-2">
        <div className={`h-4 w-1/3 animate-pulse rounded ${skelClass}`} />
        <div className={`h-3 w-1/2 animate-pulse rounded ${skelClass}`} />
      </div>
      <div className={`h-9 w-28 animate-pulse rounded-full ${skelClass}`} />
    </div>
  );

  const renderCandidateRow = (candidate: CandidateProfile) => {
    const isBookmarked = Boolean(savedCandidatesMap[candidate.id]);
    const candidateId = candidate.id;
    const profileAccountId = candidate.accountId || candidate.auraId || candidate.id;
    const profilePath = profileAccountId
      ? getPublicProfilePath(
          profileAccountId,
          candidateId ? {candidateId, fromSearch: true, source: 'search'} : {fromSearch: true, source: 'search'},
        )
      : null;
    const navigateToProfile = () => {
      if (!profilePath) return;
      window.open(profilePath, '_blank', 'noopener,noreferrer');
    };

    return (
      <div className={`flex items-center gap-4 px-5 py-4 transition ${rowHoverClass}`} key={candidate.id}>
        {candidate.profileImageUrl ? (
          <img alt={candidate.fullName} className="h-14 w-14 flex-shrink-0 rounded-full object-cover" src={candidate.profileImageUrl} />
        ) : (
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[#E9EDFF] text-lg font-semibold text-[#2678FA]">
            {candidate.fullName?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}

        <button className="min-w-0 flex-1 text-left" onClick={navigateToProfile} type="button">
          <p className={`truncate text-base font-semibold hover:underline ${primaryTextClass}`}>{candidate.fullName}</p>
          <p className={`truncate text-sm ${secondaryTextClass}`}>{candidate.headline || 'Candidate'}</p>
          {candidate.location && <p className={`mt-0.5 truncate text-xs ${mutedTextClass}`}>{candidate.location}</p>}
          {candidate.matchReason && (
            <p className="mt-1 line-clamp-2 text-xs text-[#2678FA]">
              {typeof candidate.fitScore === 'number' ? (
                <span className="font-semibold">{Math.round(candidate.fitScore)}% fit · </span>
              ) : null}
              {candidate.matchReason}
            </p>
          )}
        </button>

        <button
          aria-label={isBookmarked ? 'Unsave candidate' : 'Save candidate'}
          aria-pressed={isBookmarked}
          className={`flex-shrink-0 rounded-full p-2 transition ${
            savedCandidatesSyncing ? 'cursor-not-allowed opacity-40' : 'hover:bg-gray-100'
          }`}
          disabled={savedCandidatesSyncing || bookmarkingCandidates[candidate.id]}
          onClick={() => handleBookmarkToggle(candidate)}
          type="button">
          <img alt="Bookmark" className="h-5 w-5" src={isBookmarked ? Icons.BOOKMARK_FILLED : Icons.BOOKMARK} />
        </button>

        <Button
          className="flex-shrink-0 rounded-full px-5 text-sm font-medium"
          onClick={navigateToProfile}
          size={ButtonSize.SM}
          variant={ButtonVariant.Primary}>
          View Aura
        </Button>
      </div>
    );
  };

  const renderResults = () => {
    if (isLoading) {
      return (
        <div className={`mx-auto mt-8 max-w-4xl divide-y overflow-hidden rounded-2xl border shadow-sm ${dividerClass} ${listSurfaceClass}`}>
          {Array.from({length: pageSize}).map((_, index) => renderRowSkeleton(`candidate-skeleton-${index}`))}
        </div>
      );
    }
    if (showInitialState) {
      return (
        <div className="mt-10 flex flex-col items-center gap-5 px-10 py-16 text-center">
          <img alt="Start exploring candidates" className="h-48 w-auto" src={Images.SEMANTIC_SEARCH_NO_DATA} />
          <div className="space-y-2">
            <p className={`text-xl font-semibold ${primaryTextClass}`}>Start Exploring Candidates</p>
            <p className={`text-sm ${mutedTextClass}`}>Search with a keyword to discover candidates.</p>
          </div>
        </div>
      );
    }
    if (showEmptyState) {
      return (
        <div className="mt-10 flex flex-col items-center gap-5 px-10 py-16 text-center">
          <img alt="No candidates found" className="h-48 w-auto" src={Images.SEMANTIC_SEARCH_NO_DATA} />
          <div>
            <p className={`text-lg font-semibold ${primaryTextClass}`}>No candidates matched</p>
            <p className={`text-sm ${mutedTextClass}`}>Try another keyword or broaden your search criteria to see more candidates.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-4xl">
        <div className={`mt-8 divide-y overflow-hidden rounded-2xl border shadow-sm ${dividerClass} ${listSurfaceClass}`}>
          {candidates.map(renderCandidateRow)}
        </div>
        {hasMore && (
          <div className={`mt-4 divide-y overflow-hidden rounded-2xl ${dividerClass}`} ref={loaderRef}>
            {isFetchingMore && Array.from({length: 3}).map((_, index) => renderRowSkeleton(`candidate-skeleton-more-${index}`))}
          </div>
        )}
      </div>
    );
  };

  const savedCandidatesAction = (
    <Button
      className="rounded-full px-3 py-2 text-gray-800"
      onClick={() => navigate(ROUTES.SAVED_CANDIDATES)}
      size={ButtonSize.SM}
      startIcon={<img alt="Saved Candidates" className="h-6 w-6" src={Icons.BOOKMARK_FILLED} />}
      variant={ButtonVariant.Secondary}>
      Saved Candidates
    </Button>
  );

  return (
    <DashboardLayout
      breadcrumbs={[{label: 'Dashboard', path: ROUTES.DASHBOARD}, {label: 'Search Candidates'}]}
      contentPaddingClassName="px-0"
      sidebarOffsetClassName="ml-20"
      className="bg-[#F8FAFF]"
      headerActions={savedCandidatesAction}>
      <div className="w-full">
        <section className="relative z-20 bg-gradient-to-r from-[#E7F7FF] via-[#E9EDFF] to-[#F4E7FF] px-8 py-16">
          <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-70">
            <div className="absolute -left-24 top-10 h-48 w-48 rounded-full bg-white/40 blur-[100px]" />
            <div className="absolute right-0 top-0 h-60 w-60 rounded-full bg-[#D5E4FF] blur-[120px]" />
          </div>

          <div className="relative mx-auto flex max-w-4xl flex-col items-center text-center">
            <h1 className="text-4xl font-semibold text-gray-900 md:text-[44px]">Search Candidates</h1>
            <p className="mt-4 text-base font-medium text-gray-700 md:text-lg">
              {deepMode
                ? 'Deep search on — AI evaluates each candidate and explains the fit.'
                : 'Find the right talent by searching with job titles, skills, or keywords.'}
            </p>
          </div>

          <form className="relative mt-10 flex w-full flex-col items-center" onSubmit={handleSearchSubmit}>
            <div className="flex w-full max-w-4xl items-center gap-3 rounded-full border border-white/70 bg-white/90 px-6  backdrop-blur">
              <Input
                appearance={InputAppearance.Ghost}
                className="flex-1 text-lg text-gray-800 placeholder:text-gray-500 pl-20 py-2"
                onChange={event => setSearchQuery(event.target.value)}
                placeholder="Search Aura by role, skill, or experience"
                shape={InputShape.Pill}
                startEnhancer={<img className="" src={Icons.MAGINFYING_BLACK_SEARCH_ICON} alt="Search" />}
                endEnhancer={
                  <Button
                    className="h-12 rounded-full px-10 text-base font-semibold"
                    size={ButtonSize.MD}
                    type={ButtonType.SUBMIT}
                    variant={ButtonVariant.Primary}>
                    Search
                  </Button>
                }
                value={searchQuery}
                variant={InputVariant.Large}
              />
            </div>

            <div className="mt-5 flex w-full max-w-4xl flex-wrap items-center justify-center gap-2.5">
              <FilterPill
                label="Location"
                active={Boolean(activeFilters.location)}
                valueSummary={activeFilters.location}
                onApply={applyFilters}
                onClear={clearLocation}>
                <span className="text-sm font-semibold text-gray-700">Location</span>
                <div className="mt-2">
                  <LocationAutocomplete
                    inputClassName={inputClass}
                    onChange={setLocationInput}
                    placeholder="e.g. San Francisco"
                    value={locationInput}
                  />
                </div>
              </FilterPill>

              <FilterPill
                label="Skills"
                active={activeFilters.skills.length > 0}
                valueSummary={activeFilters.skills.join(', ')}
                onApply={applyFilters}
                onClear={clearSkills}>
                <span className="text-sm font-semibold text-gray-700">Skills</span>
                <div className="mt-2">
                  <TagAutocomplete
                    getSuggestions={getSkillSuggestions}
                    onChange={setSkillsTags}
                    placeholder="Type a skill…"
                    values={skillsTags}
                  />
                </div>
              </FilterPill>

              <FilterPill
                label="Industries"
                active={activeFilters.industries.length > 0}
                valueSummary={activeFilters.industries.join(', ')}
                onApply={applyFilters}
                onClear={clearIndustries}>
                <span className="text-sm font-semibold text-gray-700">Industries</span>
                <div className="mt-2">
                  <TagAutocomplete
                    getSuggestions={getIndustrySuggestions}
                    onChange={setIndustriesTags}
                    placeholder="Type an industry…"
                    values={industriesTags}
                  />
                </div>
              </FilterPill>

              <FilterPill
                label="Experience"
                active={experienceSummary !== undefined}
                valueSummary={experienceSummary}
                onApply={applyFilters}
                onClear={clearExperience}>
                <span className="text-sm font-semibold text-gray-700">Experience (years)</span>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    className={inputClass}
                    min={0}
                    onChange={event => setExperienceMinInput(event.target.value)}
                    placeholder="Min"
                    type="number"
                    value={experienceMinInput}
                  />
                  <span className="text-gray-400">–</span>
                  <input
                    className={inputClass}
                    min={0}
                    onChange={event => setExperienceMaxInput(event.target.value)}
                    placeholder="Max"
                    type="number"
                    value={experienceMaxInput}
                  />
                </div>
              </FilterPill>

              {hasActiveFilters && (
                <button type="button" onClick={clearAllFilters} className="ml-1 text-sm font-medium text-gray-500 hover:text-gray-700">
                  Clear all
                </button>
              )}

              <button
                type="button"
                onClick={() => setDeepMode(prev => !prev)}
                title="AI deep evaluation with reasons (slower, higher quality)"
                className={`ml-auto flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                  deepMode
                    ? 'border-transparent bg-gradient-to-r from-[#0B1220] to-[#1E3A8A] text-white shadow-lg shadow-[#1E3A8A]/30 ring-1 ring-[#1E3A8A]/40'
                    : 'border-[#2678FA]/40 bg-white text-[#2678FA] hover:bg-[#2678FA]/5'
                }`}>
                ✨ Deep search{deepMode ? ' · On' : ''}
              </button>
            </div>
          </form>
        </section>

        <section className="mt-8 px-8 pb-16 md:pb-24">
          {(error || savedCandidatesError) && (
            <div className="mt-6 space-y-2">
              {error && <div className="rounded-2xl border border-red-100 bg-red-50 px-6 py-4 text-sm text-red-600 shadow-sm">{error}</div>}
              {savedCandidatesError && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50 px-6 py-4 text-sm text-amber-800 shadow-sm">
                  {savedCandidatesError}
                </div>
              )}
            </div>
          )}

          {searchSummary && !isLoading && (
            <div className="mx-auto mb-2 max-w-4xl rounded-xl border border-[#2678FA]/20 bg-[#2678FA]/5 px-5 py-3 text-sm text-gray-700">
              <span className="font-semibold text-[#2678FA]">Deep search summary: </span>
              {searchSummary}
            </div>
          )}
          {renderResults()}
        </section>
      </div>
    </DashboardLayout>
  );
};

export default SearchCandidatesPage;
